import dayjs from "dayjs";
import { insightsDesignEnabled } from "../../common/utils/insightsDesignEnabled";
import { provName, toProv } from "../../components/Analytics/PriceReports/provinceUtils";
import {
  lookbackDateBounds,
  QUARTERLY_LOOKBACK_MAX_YEARS,
  QUARTERLY_LOOKBACK_YEAR_OPTIONS,
} from "../../services/pricingLookback";
import { insightsBarChartColor } from "./design/designTokens";
import { normalizeAggregateProductId } from "./pricingReportUtils";

export { QUARTERLY_LOOKBACK_MAX_YEARS, QUARTERLY_LOOKBACK_YEAR_OPTIONS };

export const INSIGHTS_BAR_CHART_MAX_BARS = 12;

export function resolveInsightsBarChartColor() {
  return insightsDesignEnabled() ? insightsBarChartColor : CHART_COLORS[0];
}

/**
 * @template T
 * @param {T[]} categories Sorted category keys (retailers, province codes, etc.)
 * @param {number} [maxBars]
 * @returns {T[]}
 */
export function limitOrderedCategories(categories, maxBars = INSIGHTS_BAR_CHART_MAX_BARS) {
  if (!Array.isArray(categories) || categories.length <= maxBars) return categories || [];
  return categories.slice(0, maxBars);
}

/**
 * Slice Chart.js bar chart data to at most `maxBars` categories.
 * Use `sliceFromEnd` for chronological series (most recent buckets).
 *
 * @param {{ labels?: string[], datasets?: { data?: unknown[] }[] } | null | undefined} chart
 * @param {{ maxBars?: number, sliceFromEnd?: boolean }} [options]
 */
export function limitChartJsBarChart(chart, options = {}) {
  const maxBars = options.maxBars ?? INSIGHTS_BAR_CHART_MAX_BARS;
  const sliceFromEnd = Boolean(options.sliceFromEnd);
  const labels = chart?.labels;
  if (!Array.isArray(labels) || labels.length <= maxBars) {
    return chart ?? { labels: [], datasets: [] };
  }

  const startIndex = sliceFromEnd ? labels.length - maxBars : 0;
  const endIndex = sliceFromEnd ? labels.length : maxBars;

  return {
    ...chart,
    labels: labels.slice(startIndex, endIndex),
    datasets: (chart.datasets || []).map((dataset) => ({
      ...dataset,
      data: Array.isArray(dataset.data) ? dataset.data.slice(startIndex, endIndex) : dataset.data,
    })),
  };
}

export const CHART_COLORS = [
  "#2457A5",
  "#2E7D32",
  "#C62828",
  "#6A1B9A",
  "#EF6C00",
  "#00838F",
  "#AD1457",
  "#283593",
  "#4E342E",
  "#455A64",
];

function normalizeNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function aggregateCount(row = {}) {
  if (typeof row.count === "number") return row.count;
  if (typeof row.count === "string") return normalizeNumber(row.count);
  if (row.count && typeof row.count === "object") {
    if (typeof row.count["*"] === "number") return row.count["*"];
    if (typeof row.count.unit_price === "number") return row.count.unit_price;
    const firstValue = Object.values(row.count)[0];
    return normalizeNumber(firstValue);
  }
  return 0;
}

function aggregateAverage(row = {}) {
  if (typeof row?.avg?.unit_price === "number") return row.avg.unit_price;
  if (typeof row["avg->unit_price"] === "number") return row["avg->unit_price"];
  if (typeof row.avg_unit_price === "number") return row.avg_unit_price;
  if (row.avg && typeof row.avg === "object") {
    const firstValue = Object.values(row.avg)[0];
    return normalizeNumber(firstValue);
  }
  return normalizeNumber(row.unit_price);
}

function aggregateMin(row = {}) {
  if (typeof row?.min?.unit_price === "number") return row.min.unit_price;
  if (typeof row["min->unit_price"] === "number") return row["min->unit_price"];
  if (typeof row.min_unit_price === "number") return row.min_unit_price;
  if (row.min && typeof row.min === "object") {
    const firstValue = Object.values(row.min)[0];
    return normalizeNumber(firstValue);
  }
  return null;
}

function aggregateMax(row = {}) {
  if (typeof row?.max?.unit_price === "number") return row.max.unit_price;
  if (typeof row["max->unit_price"] === "number") return row["max->unit_price"];
  if (typeof row.max_unit_price === "number") return row.max_unit_price;
  if (row.max && typeof row.max === "object") {
    const firstValue = Object.values(row.max)[0];
    return normalizeNumber(firstValue);
  }
  return null;
}

function rowYearMonth(row = {}) {
  const year = row.record_at_year ?? row.year ?? row["year(record_at)"];
  const month = row.record_at_month ?? row.month ?? row["month(record_at)"];
  const numericYear = normalizeNumber(year);
  const numericMonth = normalizeNumber(month);
  if (!numericYear || !numericMonth) return null;
  return { year: numericYear, month: numericMonth };
}

function rowYearWeek(row = {}) {
  const year = row.record_at_year ?? row.year ?? row["year(record_at)"];
  const week = row.record_at_week ?? row.week ?? row["week(record_at)"];
  const numericYear = normalizeNumber(year);
  const numericWeek = normalizeNumber(week);
  if (!numericYear || !numericWeek) return null;
  return { year: numericYear, week: numericWeek };
}

export function monthKeyFromRow(row) {
  const ym = rowYearMonth(row);
  if (!ym) return "";
  return `${ym.year}-${String(ym.month).padStart(2, "0")}`;
}

export function weekKeyFromRow(row) {
  const yw = rowYearWeek(row);
  if (!yw) return "";
  return `${yw.year}-W${String(yw.week).padStart(2, "0")}`;
}

export function timeBucketKeyFromRow(row, granularity = "month") {
  return granularity === "week" ? weekKeyFromRow(row) : monthKeyFromRow(row);
}

/**
 * Inclusive calendar months spanned by the filter range.
 * @param {string} dateFrom YYYY-MM-DD
 * @param {string} dateTo YYYY-MM-DD or ISO datetime
 */
export function inclusiveMonthSpan(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) return Number.POSITIVE_INFINITY;
  const start = dayjs(dateFrom).startOf("month");
  const end = dayjs(String(dateTo).slice(0, 10)).startOf("month");
  if (!start.isValid() || !end.isValid()) return Number.POSITIVE_INFINITY;
  return end.diff(start, "month") + 1;
}

/** Use weekly buckets when the selected range covers fewer than three months. */
export function shouldUseWeeklyTimeBuckets(dateFrom, dateTo) {
  return inclusiveMonthSpan(dateFrom, dateTo) < 3;
}

export function compareWeekKeys(a, b) {
  const parse = (key) => {
    const match = String(key).match(/^(\d{4})-W(\d{1,2})$/);
    if (!match) return { year: 0, week: 0 };
    return { year: Number(match[1]), week: Number(match[2]) };
  };
  const left = parse(a);
  const right = parse(b);
  if (left.year !== right.year) return left.year - right.year;
  return left.week - right.week;
}

export function compareTimeBucketKeys(a, b, granularity = "month") {
  return granularity === "week" ? compareWeekKeys(a, b) : compareMonthKeys(a, b);
}

function dimensionValue(row, dimension) {
  if (dimension === "product") return normalizeAggregateProductId(row);
  if (dimension === "retailer") return String(row.retailer || "").trim();
  if (dimension === "province") return String(row.province || "").trim();
  return "";
}

export function compareMonthKeys(a, b) {
  return String(a).localeCompare(String(b));
}

function makeSeriesLabel(dimension, rawValue, productNameById = new Map()) {
  if (dimension === "product") return productNameById.get(String(rawValue)) || String(rawValue);
  if (dimension === "province") {
    const code = toProv(rawValue);
    return code ? provName(code) : rawValue;
  }
  return rawValue;
}

function gatherTimeBucketSeriesGroupedData({
  rows = [],
  dimension,
  granularity = "month",
  maxSeries = null,
  productNameById = new Map(),
}) {
  const buckets = new Set();
  const seriesByEntity = new Map();

  (rows || []).forEach((row) => {
    const bucket = timeBucketKeyFromRow(row, granularity);
    const entity = dimensionValue(row, dimension);
    if (!bucket || !entity) return;

    const avg = aggregateAverage(row);
    const count = aggregateCount(row);
    const weight = count > 0 ? count : 1;

    buckets.add(bucket);
    if (!seriesByEntity.has(entity)) {
      seriesByEntity.set(entity, {
        totalCount: 0,
        points: new Map(),
      });
    }

    const series = seriesByEntity.get(entity);
    series.totalCount += count;
    if (!series.points.has(bucket)) {
      series.points.set(bucket, { weightedSum: 0, weight: 0 });
    }
    const point = series.points.get(bucket);
    point.weightedSum += avg * weight;
    point.weight += weight;
  });

  const labels = [...buckets].sort((a, b) => compareTimeBucketKeys(a, b, granularity));
  const sortedEntities = [...seriesByEntity.entries()].sort((a, b) => {
    if (b[1].totalCount !== a[1].totalCount) return b[1].totalCount - a[1].totalCount;
    return String(a[0]).localeCompare(String(b[0]));
  });
  const limited =
    Number(maxSeries) > 0 ? sortedEntities.slice(0, Number(maxSeries)) : sortedEntities;

  return { labels, limited, dimension, productNameById };
}

function bucketAverageFromSeriesPoint(point) {
  if (!point || point.weight <= 0) return null;
  return roundPrice(point.weightedSum / point.weight);
}

/**
 * Shared Y-axis range for facet mini charts so price scales stay comparable.
 * @param {{ data?: (number|null)[] }[]} facets
 */
export function computeSharedYScale(facets, { paddingRatio = 0.08 } = {}) {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (const facet of facets || []) {
    for (const value of facet.data || []) {
      if (value == null || !Number.isFinite(value)) continue;
      min = Math.min(min, value);
      max = Math.max(max, value);
    }
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return { min: 0, max: 1 };
  }

  if (min === max) {
    const pad = Math.max(1, Math.abs(min) * 0.1);
    return { min: roundPrice(min - pad), max: roundPrice(max + pad) };
  }

  const range = max - min;
  const pad = range * paddingRatio;
  return {
    min: roundPrice(min - pad),
    max: roundPrice(max + pad),
  };
}

export function facetChartHasSeriesData(facetChart) {
  return (facetChart?.facets || []).some((facet) =>
    (facet.data || []).some((value) => value != null && Number.isFinite(value))
  );
}

/**
 * Weighted average line series grouped by month or week.
 */
export function buildTimeBucketLineSeries(params = {}) {
  const { labels, limited, dimension, productNameById } = gatherTimeBucketSeriesGroupedData(params);

  const datasets = limited.map(([entity, series], index) => ({
    label: makeSeriesLabel(dimension, entity, productNameById),
    data: labels.map((bucket) => bucketAverageFromSeriesPoint(series.points.get(bucket))),
    entityValue: entity,
    borderColor: CHART_COLORS[index % CHART_COLORS.length],
    backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
    tension: 0.2,
    spanGaps: true,
  }));

  return { labels, datasets };
}

/**
 * Same grouped data as {@link buildTimeBucketLineSeries}, split into facet mini charts
 * with a shared timeline and Y-axis range.
 */
export function buildTimeBucketFacetLineSeries(params = {}) {
  const { labels, limited, dimension, productNameById } = gatherTimeBucketSeriesGroupedData(params);

  const facets = limited.map(([entity, series], index) => ({
    label: makeSeriesLabel(dimension, entity, productNameById),
    color: CHART_COLORS[index % CHART_COLORS.length],
    entityValue: entity,
    data: labels.map((bucket) => bucketAverageFromSeriesPoint(series.points.get(bucket))),
  }));

  return {
    labels,
    facets,
    yScale: computeSharedYScale(facets),
  };
}

export function buildMonthlyLineSeries(params = {}) {
  return buildTimeBucketLineSeries({ ...params, granularity: "month" });
}

export const RETAILER_COMBINED_SMOOTHING_WINDOW_MONTHS = 3;
export const COMBINED_RETAILER_HIGHLIGHT_COUNT = 3;
export const COMBINED_RETAILER_MUTED_COLOR = "rgba(158, 158, 158, 0.55)";

/**
 * Rolling average over a fixed timeline; null buckets are skipped but the
 * window still advances by calendar index so the series stays aligned.
 * @param {(number|null)[]} values
 * @param {number} [windowSize]
 */
export function applyRollingAverage(values, windowSize = RETAILER_COMBINED_SMOOTHING_WINDOW_MONTHS) {
  if (!Array.isArray(values) || windowSize < 1) return values || [];

  return values.map((_, index) => {
    const start = Math.max(0, index - windowSize + 1);
    const windowValues = values
      .slice(start, index + 1)
      .filter((value) => value != null && Number.isFinite(value));
    if (!windowValues.length) return null;
    const sum = windowValues.reduce((total, value) => total + value, 0);
    return roundPrice(sum / windowValues.length);
  });
}

function segmentSpansObservedDataGap(ctx, observedMask) {
  if (!Array.isArray(observedMask) || observedMask.length === 0) return false;
  const from = ctx.p0DataIndex;
  const to = ctx.p1DataIndex;
  if (to - from <= 1) return false;
  for (let index = from + 1; index < to; index += 1) {
    if (!observedMask[index]) return true;
  }
  return false;
}

function resolveDatasetDisplayColor(ctxOrDataset) {
  const dataset =
    ctxOrDataset?.displayColor != null || ctxOrDataset?.seriesColor != null
      ? ctxOrDataset
      : ctxOrDataset?.dataset ??
        ctxOrDataset?.chart?.data?.datasets?.[ctxOrDataset?.datasetIndex];
  if (!dataset) return COMBINED_RETAILER_MUTED_COLOR;
  return dataset.displayColor || dataset.seriesColor || COMBINED_RETAILER_MUTED_COLOR;
}

function buildCombinedRetailerSegmentStyle(observedMask) {
  return {
    borderDash: (ctx) =>
      segmentSpansObservedDataGap(ctx, observedMask) ? [6, 4] : undefined,
    borderColor: (ctx) => {
      const baseColor = resolveDatasetDisplayColor(ctx);
      if (segmentSpansObservedDataGap(ctx, observedMask)) {
        if (String(baseColor).startsWith("#")) {
          return colorWithAlpha(baseColor, 0.45);
        }
        return baseColor;
      }
      return baseColor;
    },
  };
}

function buildCombinedRetailerDataset({
  label,
  entityValue,
  seriesColor,
  labels,
  seriesPoints,
  smoothingWindow,
}) {
  const raw = labels.map((bucket) => bucketAverageFromSeriesPoint(seriesPoints.get(bucket)));
  const observedMask = raw.map((value) => value != null && Number.isFinite(value));
  const smoothed = applyRollingAverage(raw, smoothingWindow);
  const displayColor = seriesColor;

  return {
    label,
    data: smoothed,
    entityValue,
    seriesColor,
    displayColor,
    observedMask,
    borderColor: displayColor,
    backgroundColor: displayColor,
    pointBackgroundColor: displayColor,
    pointBorderColor: displayColor,
    tension: 0,
    stepped: false,
    spanGaps: true,
    pointRadius: 2,
    pointHoverRadius: 4,
    segment: buildCombinedRetailerSegmentStyle(observedMask),
  };
}

/**
 * Combined multi-retailer line chart with rolling averages, linear interpolation,
 * dashed spans across missing months, and top-series emphasis metadata.
 */
export function buildTimeBucketSmoothedCombinedLineSeries(
  params = {},
  {
    dimension = "retailer",
    smoothingWindow = RETAILER_COMBINED_SMOOTHING_WINDOW_MONTHS,
    highlightTopCount = COMBINED_RETAILER_HIGHLIGHT_COUNT,
  } = {}
) {
  const { labels, limited, productNameById } = gatherTimeBucketSeriesGroupedData({
    ...params,
    dimension,
  });

  const datasets = limited.map(([entity, series], index) =>
    buildCombinedRetailerDataset({
      label: makeSeriesLabel(dimension, entity, productNameById),
      entityValue: entity,
      seriesColor: CHART_COLORS[index % CHART_COLORS.length],
      labels,
      seriesPoints: series.points,
      smoothingWindow,
    })
  );

  return {
    labels,
    datasets,
    highlightTopCount,
  };
}

export function decorateCombinedRetailerChartForDisplay(chart, focusedDatasetIndex = null) {
  if (!chart?.datasets?.length) return chart ?? { labels: [], datasets: [] };

  const highlightTopCount = chart.highlightTopCount ?? COMBINED_RETAILER_HIGHLIGHT_COUNT;

  return {
    ...chart,
    datasets: chart.datasets.map((dataset, index) => {
      const isTopSeries = index < highlightTopCount;
      const isFocused = focusedDatasetIndex === index;
      const isProminent = focusedDatasetIndex == null ? isTopSeries : isFocused;
      const displayColor = isProminent
        ? dataset.seriesColor
        : COMBINED_RETAILER_MUTED_COLOR;

      return {
        ...dataset,
        displayColor,
        borderColor: displayColor,
        backgroundColor: displayColor,
        pointBackgroundColor: displayColor,
        pointBorderColor: displayColor,
        borderWidth: isFocused ? 3 : isProminent ? 2 : 1,
        order: isProminent ? 1 : 2,
      };
    }),
  };
}

function candlestickColorsForSeries(color) {
  const fill = colorWithAlpha(color, 0.35);
  return {
    borderColors: { up: color, down: color, unchanged: color },
    backgroundColors: { up: fill, down: fill, unchanged: fill },
    // Chart.js legend reads flat colors; financial plugin uses borderColors/backgroundColors.
    borderColor: color,
    backgroundColor: fill,
  };
}

function colorWithAlpha(hex, alpha) {
  const normalized = String(hex).replace("#", "");
  if (normalized.length !== 6) return hex;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function roundPrice(value) {
  return Number(Number(value).toFixed(2));
}

function mergeBucketStats(existing, avg, min, max, weight) {
  if (!existing) {
    const low = min ?? avg;
    const high = max ?? avg;
    return {
      weightedSum: avg * weight,
      weight,
      min: low,
      max: high,
    };
  }

  existing.weightedSum += avg * weight;
  existing.weight += weight;
  if (min != null) existing.min = Math.min(existing.min, min);
  if (max != null) existing.max = Math.max(existing.max, max);
  return existing;
}

function bucketStatsToCandlestickPoint(stats) {
  if (!stats || stats.weight <= 0) return null;
  const avg = roundPrice(stats.weightedSum / stats.weight);
  const low = roundPrice(stats.min ?? avg);
  const high = roundPrice(stats.max ?? avg);
  // Body spans low→high so the candle is visible; wicks extend to the same extrema.
  return {
    o: low,
    h: high,
    l: low,
    c: high,
    avg,
  };
}

function averageLineDataset({ label, color, labels, seriesPoints, entityValue }) {
  return {
    type: "line",
    label,
    // Financial charts disable parsing; line points must use { x, y } like the
    // official chartjs-chart-financial mixed-chart sample (docs/index.js).
    data: labels
      .map((bucket, bucketIndex) => {
        const stats = seriesPoints.get(bucket);
        if (!stats || stats.weight <= 0) return null;
        return { x: bucketIndex, y: roundPrice(stats.weightedSum / stats.weight) };
      })
      .filter(Boolean),
    entityValue,
    borderColor: color,
    backgroundColor: color,
    borderWidth: 2,
    tension: 0.2,
    spanGaps: true,
    fill: false,
    // Chart.js: higher order draws first (behind); lower order draws on top.
    order: 1,
  };
}

/**
 * Candlestick series for monthly or weekly average unit price with min/max range.
 */
export function buildTimeBucketCandlestickSeries({
  rows = [],
  dimension,
  granularity = "month",
  maxSeries = null,
  productNameById = new Map(),
  poolAsOne = false,
  pooledLabel = "Selected products",
}) {
  const buckets = new Set();
  const seriesByEntity = new Map();
  const pooledEntity = "__pooled__";

  (rows || []).forEach((row) => {
    const bucket = timeBucketKeyFromRow(row, granularity);
    const entity = poolAsOne ? pooledEntity : dimensionValue(row, dimension);
    if (!bucket || !entity) return;

    const avg = aggregateAverage(row);
    const min = aggregateMin(row);
    const max = aggregateMax(row);
    const count = aggregateCount(row);
    const weight = count > 0 ? count : 1;

    buckets.add(bucket);
    if (!seriesByEntity.has(entity)) {
      seriesByEntity.set(entity, {
        totalCount: 0,
        points: new Map(),
      });
    }

    const series = seriesByEntity.get(entity);
    series.totalCount += count;
    const existing = series.points.get(bucket);
    series.points.set(bucket, mergeBucketStats(existing, avg, min, max, weight));
  });

  const labels = [...buckets].sort((a, b) => compareTimeBucketKeys(a, b, granularity));
  const sortedEntities = [...seriesByEntity.entries()].sort((a, b) => {
    if (b[1].totalCount !== a[1].totalCount) return b[1].totalCount - a[1].totalCount;
    return String(a[0]).localeCompare(String(b[0]));
  });
  const limited = Number(maxSeries) > 0 ? sortedEntities.slice(0, Number(maxSeries)) : sortedEntities;

  const datasets = [];
  limited.forEach(([entity, series], index) => {
    const color = CHART_COLORS[index % CHART_COLORS.length];
    const seriesLabel = poolAsOne
      ? pooledLabel
      : makeSeriesLabel(dimension, entity, productNameById);
    // The financial controller has `parsing: false`, so every point must be a
    // pre-parsed object carrying a numeric `x`. Drop empty buckets entirely:
    // `getMinMax` destructures `{ x }` off each point and throws on null.
    const candleData = labels
      .map((bucket, bucketIndex) => {
        const point = bucketStatsToCandlestickPoint(series.points.get(bucket));
        return point ? { x: bucketIndex, ...point } : null;
      })
      .filter(Boolean);

    datasets.push({
      type: "candlestick",
      label: "High / low range",
      data: candleData,
      barPercentage: 0.55,
      categoryPercentage: 0.75,
      maxBarThickness: 32,
      order: 2,
      ...candlestickColorsForSeries(color),
    });
    datasets.push(
      averageLineDataset({
        label: seriesLabel,
        color,
        labels,
        seriesPoints: series.points,
        entityValue: entity,
      })
    );
  });

  return { labels, datasets };
}

export function filterAggregateRowsByLookbackYears(rows, lookbackYears) {
  const { dateFrom, dateTo } = lookbackDateBounds(lookbackYears);
  const [fromYear, fromMonth] = dateFrom.split("-").map(Number);
  const toDate = new Date(dateTo);
  const fromYearMonthIndex = fromYear * 12 + (fromMonth - 1);
  const toYearMonthIndex = toDate.getUTCFullYear() * 12 + toDate.getUTCMonth();

  return (rows || []).filter((row) => {
    const ym = rowYearMonth(row);
    if (!ym) return false;
    const rowYearMonthIndex = ym.year * 12 + (ym.month - 1);
    return rowYearMonthIndex >= fromYearMonthIndex && rowYearMonthIndex <= toYearMonthIndex;
  });
}

function countQuarterlyBuckets(rows) {
  return buildQuarterlySingleSeries(rows).labels.length;
}

/**
 * Lookback toggles that would show more quarters than the previous shorter window.
 */
export function computeAvailableQuarterlyLookbackYears(rows) {
  let previousCount = 0;
  const available = [];

  for (const years of QUARTERLY_LOOKBACK_YEAR_OPTIONS) {
    const filtered = filterAggregateRowsByLookbackYears(rows, years);
    const count = countQuarterlyBuckets(filtered);
    if (count <= 0) continue;
    if (years === QUARTERLY_LOOKBACK_YEAR_OPTIONS[0] || count > previousCount) {
      available.push(years);
      previousCount = count;
    }
  }

  return available;
}

export function buildQuarterlySingleSeriesForLookback(rows, lookbackYears) {
  return buildQuarterlySingleSeries(filterAggregateRowsByLookbackYears(rows, lookbackYears));
}

export function buildQuarterlySingleSeries(rows = []) {
  const quarterBuckets = new Map();

  (rows || []).forEach((row) => {
    const ym = rowYearMonth(row);
    if (!ym) return;
    const quarter = Math.floor((ym.month - 1) / 3) + 1;
    const key = `${ym.year}-Q${quarter}`;
    const avg = aggregateAverage(row);
    const count = aggregateCount(row);
    const weight = count > 0 ? count : 1;

    if (!quarterBuckets.has(key)) {
      quarterBuckets.set(key, {
        year: ym.year,
        quarter,
        weightedSum: 0,
        weight: 0,
      });
    }
    const bucket = quarterBuckets.get(key);
    bucket.weightedSum += avg * weight;
    bucket.weight += weight;
  });

  const ordered = [...quarterBuckets.values()].sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.quarter - b.quarter;
  });
  const limitedOrdered =
    ordered.length > INSIGHTS_BAR_CHART_MAX_BARS
      ? ordered.slice(-INSIGHTS_BAR_CHART_MAX_BARS)
      : ordered;

  return {
    labels: limitedOrdered.map((bucket) => `${bucket.year}-Q${bucket.quarter}`),
    datasets: [
      {
        label: "Quarterly average unit price",
        data: limitedOrdered.map((bucket) =>
          bucket.weight > 0 ? Number((bucket.weightedSum / bucket.weight).toFixed(2)) : null
        ),
        borderColor: resolveInsightsBarChartColor(),
        backgroundColor: resolveInsightsBarChartColor(),
        tension: 0.2,
        spanGaps: true,
      },
    ],
  };
}
